import { forwardRef } from 'react'
import { Close, FolderOutlined, InsertDriveFileOutlined } from '@mui/icons-material'
import { Box, Chip, IconButton, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  TreeItemContent,
  TreeItemGroupTransition,
  TreeItemIcon,
  TreeItemIconContainer,
  TreeItemLabel,
  TreeItemProps,
  TreeItemProvider,
  TreeItemRoot,
  useTreeItem,
  useTreeItemModel,
} from '@mui/x-tree-view-pro'
import { StructurePreviewTreeItem } from '../StructurePreview/types'

const PreviewTreeItem = forwardRef(function PreviewTreeItem(
  { id, itemId, label, disabled, children }: TreeItemProps,
  ref: React.Ref<HTMLLIElement>,
) {
  const {
    getContextProviderProps,
    getRootProps,
    getContentProps,
    getLabelProps,
    getGroupTransitionProps,
    getIconContainerProps,
    status,
  } = useTreeItem({ id, itemId, label, disabled, children, rootRef: ref })

  const item = useTreeItemModel<StructurePreviewTreeItem>(itemId)
  const isNew = item?.status === 'new'
  const ItemIcon = item?.kind === 'section' ? FolderOutlined : InsertDriveFileOutlined

  return (
    <TreeItemProvider {...getContextProviderProps()}>
      <TreeItemRoot {...getRootProps()}>
        <TreeItemContent
          {...getContentProps()}
          sx={
            isNew
              ? ({ palette }) => ({
                  bgcolor: alpha(palette.success.main, 0.12),
                  '&:hover': { bgcolor: alpha(palette.success.main, 0.2) },
                })
              : undefined
          }
          data-cy={isNew ? 'template-preview-new-item' : undefined}
        >
          <TreeItemIconContainer {...getIconContainerProps()}>
            <TreeItemIcon status={status} />
          </TreeItemIconContainer>

          <ItemIcon fontSize="small" color={isNew ? 'success' : 'action'} />

          <TreeItemLabel
            {...getLabelProps({
              sx: { fontStyle: item?.status === 'moved' ? 'italic' : undefined },
            })}
          />

          {!!item?.newDescendantCount && (
            <Tooltip
              title={`${item.newDescendantCount} neue ${
                item.newDescendantCount === 1 ? 'Eintrag wird' : 'Einträge werden'
              } hier hinzugefügt`}
            >
              <Box
                data-cy="template-preview-new-count"
                sx={{
                  minWidth: 20,
                  height: 20,
                  px: 0.75,
                  borderRadius: 10,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  boxSizing: 'border-box',
                  fontSize: 12,
                  fontWeight: 600,
                  // The caption line height pushes the digits off-center
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                +{item.newDescendantCount}
              </Box>
            </Tooltip>
          )}

          {item?.readded && (
            <Tooltip title="Bereits in der Dokumentation vorhanden, wird ein weiteres Mal hinzugefügt">
              <Chip
                label="erneut"
                size="small"
                color="warning"
                variant="outlined"
                data-cy="template-preview-readded"
              />
            </Tooltip>
          )}

          {item?.status === 'moved' && (
            <Tooltip title="Wird beim Speichern unter das neue Template verschoben">
              <Chip label="verschoben" size="small" variant="outlined" />
            </Tooltip>
          )}

          {isNew && item?.onRemove && (
            <Tooltip title="Entfernen">
              <IconButton
                size="small"
                aria-label="Entfernen"
                onClick={event => {
                  // Removing must not toggle the item's expansion
                  event.stopPropagation()
                  item.onRemove?.()
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TreeItemContent>
        {children && <TreeItemGroupTransition {...getGroupTransitionProps()} />}
      </TreeItemRoot>
    </TreeItemProvider>
  )
})

export default PreviewTreeItem
